import { SWRConfig } from "swr";

import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import { Accordion, AccordionSummary, IconButton, LinearProgress, Typography } from "@mui/material";

import { ApiAllRoutesItem, getAllFlightRoutes } from "@/api/routes";
import IntroAllRoutes from "@/components/IntroAllRoutes";
import RouteVoteDialog from "@/components/RouteVoteDialog";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import { ALL_ROUTES_PAGE_CHANGE_CONDITION } from "@/constants/eventName";
import useEvent from "@/hooks/useEvent";
import { dispatchEvent } from "@/hooks/useEventEmitter";
import useMore from "@/hooks/useMore";
import usePlatform from "@/hooks/usePlatform";
import { useQuery } from "@/hooks/useQuery";
import useThemeData from "@/hooks/useThemeData";

import FilterBar from "./components/FilterBar";
import MobileSearchBar from "./components/MobileSearchBar";
import RoutesList from "./components/RoutesList";
import RoutesListSkeleton from "./components/RoutesListSkeleton";
import SearchBar from "./components/SearchBar";

const RoutesPage = () => {
  const theme = useThemeData();

  const { isMobile } = usePlatform();

  const query = useQuery<{ programs?: string }>();

  const [searchData, setSearchData] = useState<ApiAllRoutesItem[]>([]);
  const [filterData, setFilterData] = useState<ApiAllRoutesItem[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [totalTitle, setTotalTitle] = useState("");

  const [openRouteRequest, setOpenRouteRequest] = useState(false);

  const { historyData, handleNext, hasMore } = useMore(filterData, { pageSize: 250 });

  const handleSearch = useEvent(async () => {
    try {
      setLoading(true);
      const { res } = await getAllFlightRoutes();
      setSearchData(res);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  });

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChange = useCallback((v: ApiAllRoutesItem[]) => {
    setFilterData(v);
  }, []);

  useEffect(() => {
    let text = "";
    if (searchData.length > 0) {
      if (filterData.length === searchData.length) {
        text = `${Number(searchData.length).toLocaleString()}` + " routes";
      } else {
        text =
          `${Number(filterData.length).toLocaleString()} / ${Number(
            searchData.length
          ).toLocaleString()}` + " results";
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [searchData, filterData]);

  useEffect(() => {
    dispatchEvent(ALL_ROUTES_PAGE_CHANGE_CONDITION, {
      ...(query.programs ? { programs: [query.programs] } : {}),
    });
  }, [query.programs]);

  return (
    <SWRConfig>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Routes</title>
        <meta
          name="description"
          content="Instantly access one year of availability for your favorite program routes."
        />
      </Helmet>
      <div className="max-w-5xl mx-auto">
        {isMobile ? (
          <MobileSearchBar />
        ) : (
          <div className="px-[10px] mt-[16px]">
            <SearchBar />
          </div>
        )}
        <div className="px-[10px] mt-[16px]">
          <FilterBar dataSourceList={searchData} onChange={filterChange} />
        </div>
        <div className="mt-[13px] mb-[13px]">
          <IntroAllRoutes onRouteRequest={() => setOpenRouteRequest(true)} />
        </div>

        {/* <div className="mt-[13px] mb-[13px]">
            <IntroRoutes useWhere="all-routes" onRouteRequest={() => setOpenRouteRequest(true)} />
          </div> */}

        {isLoading ? (
          <div className="mt-[-5px] mx-[10px]">
            <LinearProgress />
          </div>
        ) : null}

        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            <Typography color="textSecondary">No options matching your filters</Typography>
            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px] mt-[0px]">
            {filterData.length ? (
              <>
                <div className="flex items-center py-[12px] mt-[-8px]">
                  <Typography variant="body1" color="textPrimary">
                    {totalTitle}
                  </Typography>
                  <div className="flex-1" />
                </div>
                <RoutesList data={historyData} />

                {hasMore ? (
                  <Accordion
                    expanded={false}
                    sx={{
                      boxShadow: "none",
                      marginTop: "-1px",
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <AccordionSummary onClick={(e) => e.stopPropagation()}>
                      <div className="w-full flex items-center justify-center" onClick={handleNext}>
                        <IconButton color="primary">
                          <ExpandMoreIcon />
                        </IconButton>
                        <Typography color="textPrimary" className="mr-[10px]">
                          More Routes
                        </Typography>
                        <StarPurple500Icon fontSize="small" className="ml-[4px] mb-[1px]" />
                      </div>
                    </AccordionSummary>
                  </Accordion>
                ) : null}
              </>
            ) : null}
            {isLoading ? <RoutesListSkeleton count={10} className="mt-[10px]" /> : null}
          </div>
        )}
      </div>

      <RouteVoteDialog open={openRouteRequest} onOpen={() => setOpenRouteRequest(false)} />

      <ScrollTopBar />
    </SWRConfig>
  );
};

export default RoutesPage;
